import { motion } from 'framer-motion';
import { Mail, MessageCircle, MapPin, Github, Linkedin, Instagram, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const socialLinks = [
  { icon: Github, href: 'https://github.com/RestuAgilYA', label: 'GitHub' },
  { icon: Linkedin, href: 'https://www.linkedin.com/in/restuagilya/', label: 'LinkedIn' },
  { icon: Instagram, href: 'https://www.instagram.com/_restuagil', label: 'Instagram' },
  { icon: Mail, href: 'mailto:restuagil.ya@gmail.com', label: 'Email' },
];

export default function Contact() {
  const { t } = useLanguage();

  const contactInfo = [
    {
      icon: Mail,
      label: t.contact.email,
      value: 'restuagil.ya@gmail.com',
      href: 'mailto:restuagil.ya@gmail.com',
    },
    {
      icon: MessageCircle,
      label: t.contact.whatsapp || 'WhatsApp',
      value: '+62 889-2298-088',
      href: 'https://wa.me/628892298088',
      external: true,
    },
    {
      icon: MapPin,
      label: t.contact.location,
      value: 'Special Region of Yogyakarta, Indonesia',
      href: null,
    },
  ];

  return (
    <section id="contact" className="relative py-24 overflow-hidden">
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-150 h-150 bg-accent-500/5 rounded-full blur-3xl" />

      <div className="section-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-accent-400 font-mono text-sm mb-2">{t.contact.subtitle}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-heading mb-4">
            {t.contact.title} <span className="gradient-text">{t.contact.titleHighlight}</span>
          </h2>
          <div className="w-24 h-1 bg-linear-to-r from-accent-500 to-cyan-500 mx-auto rounded-full mb-6" />
          <p className="max-w-lg mx-auto text-muted">{t.contact.description}</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Contact Info Cards */}
          <div className="space-y-4">
            {contactInfo.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                {item.href ? (
                  <a
                    href={item.href}
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noopener noreferrer' : undefined}
                    className="group flex items-center gap-4 p-5 rounded-xl glass glass-hover"
                  >
                    <div className="w-12 h-12 rounded-xl bg-accent-500/15 flex items-center justify-center group-hover:bg-accent-500/25 transition-colors">
                      <item.icon size={22} className="text-accent-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-faint uppercase tracking-wider mb-0.5">
                        {item.label}
                      </p>
                      <p className="text-heading font-medium">{item.value}</p>
                    </div>
                    <ArrowUpRight
                      size={18}
                      className="text-faint group-hover:text-accent-400 transition-colors"
                    />
                  </a>
                ) : (
                  <div className="group flex items-center gap-4 p-5 rounded-xl glass">
                    <div className="w-12 h-12 rounded-xl bg-accent-500/15 flex items-center justify-center">
                      <item.icon size={22} className="text-accent-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-faint uppercase tracking-wider mb-0.5">
                        {item.label}
                      </p>
                      <p className="text-heading font-medium">{item.value}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* CTA Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="glass rounded-2xl p-8 glow-border flex flex-col justify-between"
          >
            <div>
              <h3 className="text-2xl font-bold text-heading mb-4">
                {t.contact.ctaTitle}{' '}
                <span className="gradient-text">{t.contact.ctaTitleHighlight}</span>
              </h3>
              <p className="text-muted leading-relaxed mb-6">{t.contact.ctaDesc}</p>
            </div>

            <div className="space-y-4">
              <a
                href="mailto:restuagil.ya@gmail.com"
                className="group flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl bg-linear-to-r from-accent-500 to-cyan-500 text-white font-semibold hover:shadow-xl hover:shadow-accent-500/25 transition-all duration-300 hover:-translate-y-0.5"
              >
                <Mail size={18} />
                {t.contact.sendEmail}
                <ArrowUpRight
                  size={16}
                  className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                />
              </a>

              <a
                href="/assets/CV/CV%20Restu%20Agil%20Yuli%20Arjun%20-%202025%20en.pdf"
                download="CV Restu Agil Yuli Arjun - 2025 en.pdf"
                className="flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl glass glass-hover text-body hover:text-heading font-semibold"
              >
                {t.contact.downloadCV}
              </a>
            </div>
          </motion.div>
        </div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex justify-center gap-4 mt-12"
        >
          {socialLinks.map(({ icon: Icon, href, label }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              className="w-12 h-12 rounded-xl glass glass-hover flex items-center justify-center text-muted hover:text-heading"
            >
              <Icon size={20} />
            </a>
          ))}
        </motion.div>
      </div>

      {/* Footer */}
      <div className="mt-20 pt-8 border-t border-surface-border">
        <div className="section-container">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted">
            <p>{t.contact.footer.by}</p>
            <p>&copy; {new Date().getFullYear()} {t.contact.footer.rights}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
